import { isEncryptedLiveEnabled } from "./encryptedRing";

/**
 * What the API publishes on `encrypted_session` (fed by the core-NATS subject
 * `encrypted.session.<orgId>`). The row itself is NOT in here on purpose —
 * grouping and distance filtering stay on the read side, so this is a trigger
 * and `recentEncrypted` is the payload.
 */
export interface EncryptedSessionEvent {
  v: number;
  kind: "opened" | "updated" | "closed";
  id: number;
  organization_id: number;
  imei: string;
  start: string;
  stop: string;
  live: boolean;
  ts: number;
}

/** What the listener knows when it decides whether to hold a stream open. */
export interface EncryptedStreamPreconditions {
  /** The auth query has not settled yet. */
  userLoading: boolean;
  /** The signed-in user, or nothing. */
  user: unknown;
  /** The active organisation, or nothing while one is being resolved. */
  organizationId: number | null | undefined;
  /** The `["encryptedToggle", orgId]` cache entry, whatever it currently holds. */
  encryptedToggle: unknown;
}

/**
 * Whether to hold an `/events/encrypted` stream open at all.
 *
 * Every condition is fail-closed, and the toggle one deliberately reuses the
 * ring gate's predicate rather than re-spelling `=== 1`: an org that has not
 * switched Encrypted Detection on must never have a stream opened for it, and
 * "we do not know yet" — the toggle query unresolved, disabled, or still
 * retrying after a failure — has to read the same way as "off". Extracted from
 * the component so that property is something a test can execute, rather than
 * something you have to re-derive by reading an effect.
 */
export function shouldOpenEncryptedSessionStream({
  userLoading,
  user,
  organizationId,
  encryptedToggle,
}: EncryptedStreamPreconditions): boolean {
  return (
    !userLoading &&
    !!user &&
    organizationId != null &&
    isEncryptedLiveEnabled(encryptedToggle)
  );
}

/** The one shape of a browser MessageEvent this reader needs. */
export interface EncryptedSessionSseEvent {
  data: string;
  lastEventId: string;
}

export interface EncryptedSessionReaderOptions {
  /**
   * Something moved — refresh the list. Fires for every accepted event,
   * including one whose payload could not be read: "something changed" must
   * not depend on being able to read WHAT changed.
   */
  onActivity: () => void;
  /** This session ended; its live badge can go out now. */
  onClosed: (id: number) => void;
  /** This session's dialog is on screen, so its detail queries need re-reading. */
  onOpenSessionChanged: (id: number) => void;
  /** Which session's dialog is open right now, if any. */
  openSessionId: () => number | null;
}

export interface EncryptedSessionReader {
  read: (event: EncryptedSessionSseEvent) => void;
  /** New connection: the server may replay its buffer, so accept old ids again. */
  reset: () => void;
}

/**
 * Turns raw `encrypted_session` SSE events into the three decisions the
 * listener acts on, and owns the monotonic de-duplication that a replaying
 * server makes necessary.
 *
 * Separate from the component so the guards are testable — the interesting
 * cases (a replayed id, an event with no id at all, an unreadable payload) are
 * all failure modes that would otherwise only show up as "live updates went
 * quiet" in production.
 */
export function createEncryptedSessionReader({
  onActivity,
  onClosed,
  onOpenSessionChanged,
  openSessionId,
}: EncryptedSessionReaderOptions): EncryptedSessionReader {
  let lastAppliedEventId = 0;

  return {
    reset() {
      lastAppliedEventId = 0;
    },

    read(event) {
      // An event with no `id:` line has lastEventId "" — which Number() turns
      // into 0, i.e. "already applied". Such an event carries no ordering
      // information at all, so the only safe reading is "new".
      if (event.lastEventId !== "") {
        const rawEventId = Number(event.lastEventId);
        if (Number.isFinite(rawEventId)) {
          const eventId = Math.trunc(rawEventId);
          if (eventId <= lastAppliedEventId) return;
          lastAppliedEventId = eventId;
        }
      }

      onActivity();

      let payload: EncryptedSessionEvent | null = null;
      try {
        payload = JSON.parse(event.data) as EncryptedSessionEvent;
      } catch {
        return;
      }

      const id = Math.trunc(Number(payload?.id));
      if (!Number.isFinite(id) || id <= 0) return;

      if (payload.kind === "closed") onClosed(id);
      if (openSessionId() === id) onOpenSessionChanged(id);
    },
  };
}
